import { Binoculars } from "@phosphor-icons/react"
import { ErrorBoundary } from "@sentry/nextjs"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { BASE_PATH } from "@/constants/app.mjs"
import { productPageLogos } from "@/constants/productPageLogos"
import NicerBlockquote from "../../common/NicerBlockquote"
import ProductPageHeader from "../ProductPageHeader"
import ProductPageTemplate from "../ProductPageTemplate"
import { CustomerLogoStrip, FeatureCard, StatsGrid, ThreeFeaturesSection } from "../shared"
import { FinancialStatementWrapper } from "./FinancialStatementWrapper"
import { MethodologyCard } from "./MethodologyCard"
import ProTierCallout from "./ProTierCallout"
import { ScreenerTablePreview } from "./ScreenerTablePreview"

const ExplorerPage = () => {
  // Feature 1: Screener Table (real-time data from API)
  const ScreenerTableFeature = (
    <FeatureCard>
      <div className="w-full">
        <ErrorBoundary fallback={<div>Error loading screener table</div>}>
          <ScreenerTablePreview />
        </ErrorBoundary>
      </div>
    </FeatureCard>
  )

  // Feature 2: Financial Statement (hardcoded to Solana)
  const FinancialStatementFeature = (
    <FeatureCard className="h-[300px]">
      <FinancialStatementWrapper projectIds={["solana"]} selectedProjectId="solana" />
    </FeatureCard>
  )

  // Feature 3: Methodology Card
  const MethodologyFeature = (
    <FeatureCard className="h-[300px]">
      <MethodologyCard />
    </FeatureCard>
  )

  return (
    <ProductPageTemplate
      header={
        <ProductPageHeader
          icon={Binoculars}
          title="Explorer"
          subtitle="A no-code interface for browsing standardized financial and usage metrics for thousands of projects and tokenized assets across 100+ blockchains."
          ctaButtons={
            <>
              <LinkButton
                key="start-free"
                size="large"
                variant="solid"
                href="/explorer"
                style={{ zIndex: 4 }}
              >
                Start free
              </LinkButton>
              <LinkButton
                key="view-pricing"
                size="large"
                variant="ghost"
                href="/pricing"
                style={{ zIndex: 4 }}
              >
                View pricing
              </LinkButton>
            </>
          }
          backgroundImageUrl={`/${BASE_PATH}static/images/marketing/home/hero-illustration-mountains2.png`}
          whoWeServeText="Investors, analysts, and anyone researching projects - no technical skills required."
          simpleWhoWeServeText="investors, analysts, and anyone researching projects"
        />
      }
      productId="explorer"
    >
      {/* Customer Logo Strip */}
      <CustomerLogoStrip logos={productPageLogos.explorer} />

      {/* Three Features Section */}
      <ThreeFeaturesSection
        features={[
          {
            component: ScreenerTableFeature,
            title: "The most powerful explorer in crypto",
            description:
              "Make use of over 50+ on-chain metrics to evaluate blockchains, applications, and tokenized assets.",
          },
          {
            component: FinancialStatementFeature,
            title: "Curated data sets ready to be used",
            description:
              "View income statements and cohort analysis or navigate to our dedicated section for tokenized assets.",
          },
          {
            component: MethodologyFeature,
            title: "Transparent and auditable methodologies",
            description:
              "Every metric uses a transparent and auditable methodology, no black boxes.",
          },
        ]}
      />

      {/* Stats Grid */}
      <div className="mt-32">
        <StatsGrid
          stats={[
            {
              value: "100+",
              label: "Chains",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/ethereum.png`, alt: "Ethereum" },
                { src: `${BASE_PATH}/static/images/marketing/logos/solana.png`, alt: "Solana" },
                { src: `${BASE_PATH}/static/images/marketing/logos/base.png`, alt: "Base" },
                { src: `${BASE_PATH}/static/images/marketing/logos/tron.png`, alt: "Tron" },
              ],
            },
            {
              value: "1,200+",
              label: "Apps",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/aave.png`, alt: "Aave" },
                { src: `${BASE_PATH}/static/images/marketing/logos/uniswap.png`, alt: "Uniswap" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/hyperliquid.png`,
                  alt: "Hyperliquid",
                },
                { src: `${BASE_PATH}/static/images/marketing/logos/sky.png`, alt: "Sky" },
              ],
            },
            {
              value: "7,000+",
              label: "Tokenized assets",
              logos: [
                { src: `${BASE_PATH}/static/images/marketing/logos/usdt.png`, alt: "USDT" },
                { src: `${BASE_PATH}/static/images/marketing/logos/usdc.png`, alt: "USDC" },
                { src: `${BASE_PATH}/static/images/marketing/logos/blackrock.png`, alt: "BUIDL" },
                {
                  src: `${BASE_PATH}/static/images/marketing/logos/robinhood.png`,
                  alt: "Robinhood",
                },
              ],
            },
            {
              value: "2+ PB",
              label: "Of data",
              hasSparkline: true,
            },
          ]}
        />
      </div>

      {/* Customer Quote */}
      <div className="max-w-[900px] mx-auto mt-32">
        <NicerBlockquote
          content="We're frequent and happy users of the data and dashboards available on Token Terminal. Token Terminal has done a great job focusing on standardizing KPIs that make fundamental analysis of digital assets faster and easier."
          author="Cosmo Jiang"
          authorTitle="Pantera"
          authorImage={`/${BASE_PATH}static/images/marketing/home/testimonials/avatar-cosmo_jiang.png`}
        />
      </div>

      {/* CTA Section */}
      <ProTierCallout />
    </ProductPageTemplate>
  )
}

export default ExplorerPage
